import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Button,
  Modal,
  Input,
  message,
  Card,
  Space,
  Typography,
  Spin,
  Row,
  Col,
  Statistic,
  theme,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function Offboard() {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [completedRequests, setCompletedRequests] = useState([]);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [approveReason, setApproveReason] = useState("");
  const [approving, setApproving] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [pendingPageSize, setPendingPageSize] = useState(10);
  const [completedPageSize, setCompletedPageSize] = useState(10);

  // Fetch offboard requests
  const fetchOffboardRequests = async () => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem("token");
      const response = await fetch("/api/systems/offboard-requests", {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const result = await response.json();

      if (result.success) {
        // Backend returns: pendingApproval, pendingProcess, failed, completed
        // Combine pendingApproval + pendingProcess + failed into pending list
        const pending = [
          ...(result.data.pendingApproval || []),
          ...(result.data.pendingProcess || []),
          ...(result.data.failed || []),
        ];
        setPendingRequests(pending);
        setCompletedRequests(result.data.completed || []);
      } else {
        message.error(result.message || "Failed to fetch offboard requests");
      }
    } catch (error) {
      console.error("Error fetching offboard requests:", error);
      message.error("Failed to fetch offboard requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffboardRequests();
  }, []);

  // Handle approve button click
  const handleApproveClick = (record) => {
    setSelectedSystem(record);
    setApproveReason("");
    setApproveModalVisible(true);
  };

  // Process offboard via third-party API
  const processOffboard = async (systemId) => {
    const authToken = localStorage.getItem("token");
    const response = await fetch(`/api/systems/${systemId}/offboard-process`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
    });
    return response.json();
  };

  // Handle approve confirmation — approve then auto-process
  const handleApproveConfirm = async () => {
    if (!approveReason.trim()) {
      message.error("Please enter a reason for offboarding");
      return;
    }

    setApproving(true);
    try {
      const authToken = localStorage.getItem("token");
      // Step 1: Approve
      const approveRes = await fetch(`/api/systems/${selectedSystem.id}/offboard-approve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ reason: approveReason }),
      });
      const approveResult = await approveRes.json();

      if (!approveResult.success) {
        message.error(approveResult.message || "Failed to approve offboard");
        return;
      }

      // Step 2: Auto-process (call third-party API, set status, remove from groups)
      const processResult = await processOffboard(selectedSystem.id);

      if (processResult.success) {
        message.success("System offboarded successfully");
      } else {
        // Approve succeeded but process failed — user can retry from the list
        message.warning(`Approved but API processing failed: ${processResult.message}. You can retry from the list.`);
      }

      setApproveModalVisible(false);
      setSelectedSystem(null);
      setApproveReason("");
      fetchOffboardRequests();
    } catch (error) {
      console.error("Error in offboard flow:", error);
      message.error("Failed to offboard system");
    } finally {
      setApproving(false);
    }
  };

  // Handle process/retry for already-approved or failed systems
  const handleProcess = async (systemId) => {
    setProcessingId(systemId);
    try {
      const result = await processOffboard(systemId);
      if (result.success) {
        message.success("System offboarded successfully");
      } else {
        message.error(result.message || "Processing failed");
      }
      fetchOffboardRequests();
    } catch (error) {
      message.error("Failed to process offboard");
    } finally {
      setProcessingId(null);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Pending requests table columns
  const pendingColumns = [
    {
      title: "Serial Number",
      dataIndex: "sn",
      key: "sn",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "NMI",
      dataIndex: "nmi",
      key: "nmi",
    },
    {
      title: "Customer",
      key: "customer",
      render: (_, record) => (
        <div>
          <div>{record.customer_name || "-"}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.customer_email || ""}
          </Text>
        </div>
      ),
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
    },
    {
      title: "Request Date",
      dataIndex: "offboard_requested_date",
      key: "offboard_requested_date",
      render: (date) => formatDate(date),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        if (record.offboard_api_response) {
          return <Tag color="error" icon={<ExclamationCircleOutlined />}>API Failed</Tag>;
        }
        if (record.offboard_approval_date) {
          return <Tag color="processing" icon={<ClockCircleOutlined />}>Approved — Pending Process</Tag>;
        }
        return <Tag color="warning" icon={<ClockCircleOutlined />}>Pending Approval</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, record) => {
        // Not yet approved → show Approve button
        if (!record.offboard_approval_date) {
          return (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => handleApproveClick(record)}
              style={{ borderRadius: 2 }}
            >
              Approve
            </Button>
          );
        }
        // Approved but not yet processed, or failed → show Process/Retry button
        if (!record.offboard_date) {
          return (
            <Button
              icon={<SyncOutlined />}
              loading={processingId === record.id}
              onClick={() => handleProcess(record.id)}
              style={{ borderRadius: 2 }}
            >
              {record.offboard_api_response ? "Retry" : "Process"}
            </Button>
          );
        }
        return null;
      },
    },
  ];

  // Completed requests table columns
  const completedColumns = [
    {
      title: "Serial Number",
      dataIndex: "sn",
      key: "sn",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "NMI",
      dataIndex: "nmi",
      key: "nmi",
    },
    {
      title: "Customer",
      key: "customer",
      render: (_, record) => (
        <div>
          <div>{record.customer_name || "-"}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.customer_email || ""}
          </Text>
        </div>
      ),
    },
    {
      title: "Request Date",
      dataIndex: "offboard_requested_date",
      key: "offboard_requested_date",
      render: (date) => formatDate(date),
    },
    {
      title: "Offboard Date",
      dataIndex: "offboard_date",
      key: "offboard_date",
      render: (date) => formatDate(date),
    },
    {
      title: "Reason",
      dataIndex: "offboard_reason",
      key: "offboard_reason",
      ellipsis: true,
    },
    {
      title: "Status",
      key: "status",
      render: () => (
        <Tag color="success" icon={<CheckCircleOutlined />}>
          Completed
        </Tag>
      ),
    },
  ];

  if (loading) {
    return (
      <PageLayout active="offboard">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="offboard">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              Offboard Requests
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Review and manage system offboard applications
            </Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={fetchOffboardRequests}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card bordered={false} style={{ borderRadius: 8 }}>
            <Statistic
              title="Pending Requests"
              value={pendingRequests.length}
              valueStyle={{ color: token.colorWarning }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false} style={{ borderRadius: 8 }}>
            <Statistic
              title="Completed This Month"
              value={completedRequests.filter((r) => {
                const date = new Date(r.offboard_date);
                const now = new Date();
                return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
              }).length}
              valueStyle={{ color: token.colorSuccess }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false} style={{ borderRadius: 8 }}>
            <Statistic
              title="Total Completed"
              value={completedRequests.length}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Pending Requests Table */}
      <Card
        title={
          <Space>
            <ClockCircleOutlined style={{ color: token.colorWarning }} />
            <span>Pending Requests</span>
            <Tag color="warning">{pendingRequests.length}</Tag>
          </Space>
        }
        bordered={false}
        style={{ marginBottom: 24, borderRadius: 8 }}
      >
        <Table
          columns={pendingColumns}
          dataSource={pendingRequests}
          rowKey="id"
          pagination={makePagination(pendingPageSize, setPendingPageSize)}
          locale={{ emptyText: "No pending offboard requests" }}
        />
      </Card>

      {/* Completed Requests Table */}
      <Card
        title={
          <Space>
            <CheckCircleOutlined style={{ color: token.colorSuccess }} />
            <span>Completed</span>
            <Tag color="success">{completedRequests.length}</Tag>
          </Space>
        }
        bordered={false}
        style={{ borderRadius: 8 }}
      >
        <Table
          columns={completedColumns}
          dataSource={completedRequests}
          rowKey="id"
          pagination={makePagination(completedPageSize, setCompletedPageSize)}
          locale={{ emptyText: "No completed offboard requests" }}
        />
      </Card>

      {/* Approve Modal */}
      <Modal
        title={null}
        open={approveModalVisible}
        onCancel={() => {
          setApproveModalVisible(false);
          setSelectedSystem(null);
          setApproveReason("");
        }}
        footer={null}
        centered
        width={480}
      >
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          {/* Warning Icon */}
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #faad14 0%, #ffc53d 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
              boxShadow: "0 4px 12px rgba(250, 173, 20, 0.3)",
            }}
          >
            <ExclamationCircleOutlined style={{ fontSize: 36, color: "#fff" }} />
          </div>

          <Title level={4} style={{ marginBottom: 8 }}>
            Confirm Offboard
          </Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
            Are you sure you want to offboard this system? This action cannot be undone.
          </Text>

          {/* System Info */}
          {selectedSystem && (
            <div
              style={{
                background: "#fafafa",
                borderRadius: 8,
                padding: 16,
                marginBottom: 24,
                textAlign: "left",
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    SERIAL NUMBER
                  </Text>
                  <div style={{ fontWeight: 600 }}>{selectedSystem.sn}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    NMI
                  </Text>
                  <div style={{ fontWeight: 600 }}>{selectedSystem.nmi}</div>
                </Col>
              </Row>
              {selectedSystem.customer_name && (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    CUSTOMER
                  </Text>
                  <div style={{ fontWeight: 600 }}>{selectedSystem.customer_name}</div>
                </div>
              )}
            </div>
          )}

          {/* Reason Input */}
          <div style={{ textAlign: "left", marginBottom: 24 }}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Offboard Reason <Text type="danger">*</Text>
            </Text>
            <TextArea
              rows={3}
              placeholder="Enter the reason for offboarding this system..."
              value={approveReason}
              onChange={(e) => setApproveReason(e.target.value)}
              style={{ borderRadius: 2 }}
            />
          </div>

          {/* Action Buttons */}
          <Space size={12}>
            <Button
              size="large"
              onClick={() => {
                setApproveModalVisible(false);
                setSelectedSystem(null);
                setApproveReason("");
              }}
              style={{ borderRadius: 2, minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              danger
              loading={approving}
              onClick={handleApproveConfirm}
              style={{ borderRadius: 2, minWidth: 100 }}
            >
              Confirm Offboard
            </Button>
          </Space>
        </div>
      </Modal>
    </PageLayout>
  );
}
